import { useCallback } from "react";
import { useTranslator } from "@eo-locale/react";

import { Dialog, Portal } from "@/storybook-components";

import GuestLoginOptions from "./guest-login-steps/guest-login-options";
import EmailLoginForm from "./guest-login-steps/email-login-form";
import OtpLogin from "./guest-login-steps/otp-login";
import ProfileGateForm from "./guest-login-steps/profile-gate-form";
import { resetGuestLogin } from "@/redux/features/user/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { resetAnswers, clearAllErrors } from "@/redux/features/questions/slice";
import {
  goToStep,
  closeAuthGate,
  type AuthGateStep
} from "@/redux/features/profile-gate/slice";
import { fetchUser } from "@/redux/features/user/thunk";
import {
  clearGuestOptionalProfilePromptShown,
  markGuestOptionalProfilePromptShown
} from "@/helpers/guest-profile-prompt-session";
import { recaptchaPersistentElements } from "@/helpers/recaptcha-overlay";
import useRecaptchaOverlayPointerEvents from "@/hooks/useRecaptchaOverlayPointerEvents";

const guestLoginHeaders: Record<string, string> = {
  options: "profileGate.verifyYourEmailToContinue",
  "email-login": "profileGate.continueWithEmail",
  "otp-login": "profileGate.verifyYourEmail",
  "profile-gate": "profileGate.completeYourProfileToContinue"
};

const AuthGateModal = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const guestUserId = useAppSelector(
    (state) => state.user.registeredUser.profile?.id
  );
  const {
    isOpen: open,
    currentStep,
    profileGateMode,
    reason,
    extraQueryParams,
    skippedOptionsStep
  } = useAppSelector((state) => state.authGate);

  useRecaptchaOverlayPointerEvents(open);

  const isProfileGateStep = currentStep === "profile-gate";
  const isOTPLoginStep = currentStep === "otp-login";
  const canDismiss = !isOTPLoginStep && !isProfileGateStep;
  // Optional mode is a post-contribution nudge, so we lead with a
  // "thanks" framing instead of the required-mode "complete to continue".
  const modalTitleKey =
    isProfileGateStep && profileGateMode === "optional"
      ? "profileGate.thanksForYourContribution"
      : guestLoginHeaders[currentStep] || "";

  const handleClose = () => {
    if (!canDismiss) return;
    const optionalProfileDismiss =
      isProfileGateStep && profileGateMode === "optional";
    dispatch(closeAuthGate());
    dispatch(resetAnswers());
    dispatch(clearAllErrors());
    if (!optionalProfileDismiss) {
      dispatch(resetGuestLogin());
    }
  };

  const handleGoToStep = (step: AuthGateStep) => {
    dispatch(goToStep(step));
  };

  const handleEmailLogin = () => {
    dispatch(resetGuestLogin());
    handleGoToStep("email-login");
  };

  const handleBackToOptions = () => {
    handleGoToStep("options");
  };

  const handleBackFromEmailLogin = () => {
    if (skippedOptionsStep) {
      handleClose();
      return;
    }
    handleBackToOptions();
  };

  const handleProfileGateComplete = async () => {
    await dispatch(fetchUser({}));
    clearGuestOptionalProfilePromptShown(guestUserId);
    dispatch(closeAuthGate());
  };

  const handleOptionalProfileGateDismiss = () => {
    markGuestOptionalProfilePromptShown(guestUserId);
    dispatch(closeAuthGate());
    dispatch(resetAnswers());
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "options":
        return (
          <GuestLoginOptions
            onEmailLogin={handleEmailLogin}
            extraQueryParams={extraQueryParams}
          />
        );

      case "email-login":
        return (
          <EmailLoginForm
            onBack={handleBackFromEmailLogin}
            onNext={() => handleGoToStep("otp-login")}
          />
        );

      case "otp-login":
        return (
          <OtpLogin
            onBack={handleEmailLogin}
            extraQueryParams={extraQueryParams}
          />
        );

      case "profile-gate":
        return (
          <ProfileGateForm
            reason={reason}
            onComplete={handleProfileGateComplete}
            onDismissOptional={handleOptionalProfileGateDismiss}
          />
        );

      default:
        return null;
    }
  };

  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (!details.open) {
        handleClose();
      }
    },
    [handleClose]
  );

  return (
    <Dialog.Root
      open={open}
      onOpenChange={handleOpenChange}
      placement="center"
      size="md"
      scrollBehavior="inside"
      closeOnInteractOutside={canDismiss}
      closeOnEscape={canDismiss}
      persistentElements={recaptchaPersistentElements}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onScroll={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Dialog.Header>{translate(modalTitleKey)}</Dialog.Header>
            {canDismiss && <Dialog.CloseTrigger />}
            <Dialog.Body pb={isProfileGateStep ? 0 : 4}>
              {renderStepContent()}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default AuthGateModal;
